import {
  FEEDS,
  TRIMET_TRAM_AGENCY_ID,
  TRIMET_PSC_AGENCY_ID,
  TRAM_AGENCY_ID,
  PSC_AGENCY_ID,
  ROUTE_PROVIDER_CONFIG,
  TRIMET_FEED_ID,
  type RouteProviderConfig,
  TRIMET_GTFS_AGENCY_IDS,
} from '#/shared/constants';
import {
  AERIAL_TRAM_SUB_TYPE,
  STREETCAR_SUB_TYPE,
} from '#/transit-tracker/constants';
import type { Route } from '#/types/Routes';
import { parseGtfsId } from '#/shared/utils/gtfs-id';

// Provider key mapping helpers to keep URL-safe keys consistent
const FULL_TO_URL_PROVIDER_KEY: Record<string, string> = {
  [TRIMET_PSC_AGENCY_ID]: PSC_AGENCY_ID,
  [TRIMET_TRAM_AGENCY_ID]: TRAM_AGENCY_ID,
};

const URL_TO_FULL_PROVIDER_KEY: Record<string, string> = {
  [PSC_AGENCY_ID]: TRIMET_PSC_AGENCY_ID,
  [TRAM_AGENCY_ID]: TRIMET_TRAM_AGENCY_ID,
};

export interface Provider {
  agencyId?: string;
  feedId?: string;
}

/** Types */

/**
 * Parses route parameters from URL format "feedId:routeId" or just "routeId"
 */
export const parseRouteParams = (
  urlParam?: string | null
): { routeId: string | null; feedId: string | null } => {
  const raw = urlParam?.trim() ?? '';
  if (!raw) return { routeId: null, feedId: null }; // no route = no feed
  const parsed = parseGtfsId(raw);
  if (!parsed?.feedId) return { routeId: raw, feedId: FEEDS.TRIMET }; // backwards compat default if no feedId specified
  return { routeId: parsed.id, feedId: parsed.feedId };
};

/**
 * Converts a provider object to a simplified URL-safe key.
 * Removes "TRIMET:" prefix for PSC and TRAM in URLs for cleaner URLs.
 */
export const getUrlProviderKey = ({ agencyId, feedId }: Provider): string => {
  const key: string = agencyId ?? feedId ?? '';
  return FULL_TO_URL_PROVIDER_KEY[key] ?? key;
};

/**
 * Finds a provider by its URL key, converting simplified keys back to full keys.
 */
export const findProviderByUrlKey = (
  urlKey: string,
  providerConfig: Provider[]
): Provider | undefined => {
  const lookupKey = URL_TO_FULL_PROVIDER_KEY[urlKey] ?? urlKey;
  return providerConfig.find(
    ({ agencyId, feedId }) => (agencyId ?? feedId) === lookupKey
  );
};

/**
 * Parses stop parameters from URL format "feedId:stopId" or just "stopId".
 */
export const parseStopParams = (
  urlParam: string | null | undefined
): { stopId: string | null; stopFeedId: string } => {
  const raw = urlParam?.trim() ?? '';
  if (!raw) return { stopId: null, stopFeedId: FEEDS.TRIMET };
  const parsed = parseGtfsId(raw);
  if (!parsed?.feedId) return { stopId: raw, stopFeedId: FEEDS.TRIMET };
  return { stopId: parsed.id, stopFeedId: parsed.feedId };
};

interface GetDisplayNameArgs extends Provider {
  // Whether or not to include TriMet, Streetcar, and Aerial Tram in displayName; off by default to suppress duplicate information
  includeTrimet?: boolean;
}

export const getDisplayNameFromProvider = ({
  agencyId,
  feedId,
  includeTrimet = false,
}: GetDisplayNameArgs): string | undefined => {
  if (!includeTrimet && feedId === TRIMET_FEED_ID) {
    return '';
  }
  const agencyLookup = URL_TO_FULL_PROVIDER_KEY[agencyId ?? ''] ?? agencyId;

  const providerConfig = ROUTE_PROVIDER_CONFIG.find(
    provider => provider.agencyId === agencyLookup || provider.feedId === feedId
  );

  return providerConfig?.displayName;
};

/**
 * Formats a stop ID with its feed namespace in the format "feedId:stopId".
 * Defaults to TRIMET feed if feedId is not provided.
 *
 * @param {Object} stop - Stop object containing stopId and optional feedId
 * @param {string} stop.stopId - The stop identifier
 * @param {string} [stop.feedId] - The feed identifier (defaults to FEEDS.TRIMET if not provided)
 * @returns {string} Namespaced stop ID in format "feedId:stopId"
 */
export const formatNamespacedStopId = ({
  stopId,
  feedId,
}: {
  stopId: string | number;
  feedId?: string;
}): string => {
  return `${feedId ?? FEEDS.TRIMET}:${stopId}`;
};

// Id can be either an agencyId or feedId
export const getProviderConfigById = (
  id: string
): RouteProviderConfig | undefined => {
  const agencyLookup = URL_TO_FULL_PROVIDER_KEY[id ?? ''] ?? id;
  return ROUTE_PROVIDER_CONFIG.find(provider => {
    return (
      agencyLookup === provider.agencyId || agencyLookup === provider.feedId
    );
  });
};

// Route helpers
export function getProviderKeyFromRoute(route: Route): string {
  let providerKey = route.feedId ?? FEEDS.TRIMET;

  if (route.feedId === FEEDS.TRIMET) {
    // Handle TriMet sub-agencies (Streetcar, Aerial Tram) by route ID
    if (route.routeSubType === STREETCAR_SUB_TYPE) {
      providerKey = TRIMET_GTFS_AGENCY_IDS.Streetcar;
    } else if (route.routeSubType === AERIAL_TRAM_SUB_TYPE) {
      providerKey = TRIMET_GTFS_AGENCY_IDS.AerialTram;
    }
  }

  return providerKey;
}

export function getProviderPriorityFromRoute(route: Route): number | null {
  const providerKey = getProviderKeyFromRoute(route);

  if (providerKey === FEEDS.TRIMET) return 0;
  if (providerKey === FEEDS.CTRAN) return 1;
  if (providerKey === TRIMET_GTFS_AGENCY_IDS.Streetcar) return 2;
  if (providerKey === TRIMET_GTFS_AGENCY_IDS.AerialTram) return 3;

  return null;
}

export const compareProviderPriority = (a: Route, b: Route): number => {
  const priorityOfRouteA = getProviderPriorityFromRoute(a);
  const priorityOfRouteB = getProviderPriorityFromRoute(b);

  // Both prioritized
  if (
    priorityOfRouteA !== null &&
    priorityOfRouteB !== null &&
    priorityOfRouteA !== priorityOfRouteB
  ) {
    return priorityOfRouteA - priorityOfRouteB;
  }

  // Only A prioritized → A first
  if (priorityOfRouteA !== null && priorityOfRouteB === null) return -1;

  // Only B prioritized → B first
  if (priorityOfRouteA === null && priorityOfRouteB !== null) return 1;

  // Both unprioritized → alphabetical fallback
  if (priorityOfRouteA === null && priorityOfRouteB === null) {
    return (a.providerName ?? '').localeCompare(b.providerName ?? '');
  }

  // Same priority → keep existing order (Fuse relevance etc.)
  return 0;
};

export function attachProviderMetadataToRoutes<T extends Route>(
  routes: T[]
): (T & {
  providerName: string;
  providerDisplayName?: string;
})[] {
  return routes.map(route => {
    const provider = ROUTE_PROVIDER_CONFIG.find(
      p =>
        p.feedId === route.feedId &&
        (p.agencyId == null || p.agencyId === route.agencyId)
    );

    return {
      ...route,
      providerName: provider?.name ?? '',
      providerDisplayName: provider?.displayName ?? '',
    };
  });
}
